import React from 'react';
import { Cookie, Shield, Eye, Settings, Database, Bell } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet';

const CookiePolicy = () => {
  const cookieTypes = [
    {
      icon: Shield,
      name: 'Essential Cookies',
      color: 'from-cyan-500 to-blue-500',
      description: 'Required for the website to function properly. Cannot be disabled.',
      examples: ['Authentication', 'Security', 'Session management'],
    },
    {
      icon: Settings,
      name: 'Functional Cookies',
      color: 'from-turquoise-500 to-teal-500',
      description: 'Enable enhanced functionality and personalization.',
      examples: ['Language preferences', 'Region settings', 'UI customizations'],
    },
    {
      icon: Eye,
      name: 'Analytics Cookies',
      color: 'from-blue-500 to-indigo-500',
      description: 'Help us understand how you use our website to improve user experience.',
      examples: ['Page views', 'Click tracking', 'User behavior'],
    },
    {
      icon: Bell,
      name: 'Marketing Cookies',
      color: 'from-yellow-500 to-orange-500',
      description: 'Used to deliver personalized advertisements and track campaign effectiveness.',
      examples: ['Ad targeting', 'Campaign tracking', 'Social media integration'],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Cookie Policy - Fluxara</title>
        <meta name="description" content="Learn about how Fluxara uses cookies to improve your experience." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 py-12 px-4 gradient-mesh">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-100 to-turquoise-100 rounded-3xl mb-6">
              <Cookie className="w-10 h-10 text-cyan-600" />
            </div>
            <h1 className="text-5xl font-black text-gray-900 mb-4">
              Cookie <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600">Policy</span>
            </h1>
            <p className="text-lg text-gray-600">Last updated: January 11, 2025</p>
          </div>

          {/* Introduction */}
          <Card className="glass shadow-xl border-0 mb-8">
            <CardContent className="p-8">
              <div className="border-l-4 border-cyan-500 pl-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">What Are Cookies?</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use Fluxara.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We use cookies responsibly and transparently. You have full control over which cookies you accept.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cookie Types */}
          <div className="mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">Types of Cookies We Use</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {cookieTypes.map((type, index) => {
                const Icon = type.icon;
                return (
                  <Card key={index} className="glass border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <CardContent className="p-8">
                      <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${type.color} rounded-2xl mb-4`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{type.name}</h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">{type.description}</p>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-700">Examples:</p>
                        <ul className="space-y-1">
                          {type.examples.map((example, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Detailed Information */}
          <div className="space-y-8">
            <Card className="glass border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Database className="w-6 h-6 text-cyan-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Cookie Data We Collect</h2>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="leading-relaxed">
                    When you use Fluxara, we may collect the following information through cookies:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-600 font-bold">•</span>
                      <span>Account authentication and login status</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-600 font-bold">•</span>
                      <span>Your design preferences and settings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-600 font-bold">•</span>
                      <span>Usage patterns and feature interactions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-600 font-bold">•</span>
                      <span>Device and browser information</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-600 font-bold">•</span>
                      <span>IP address and general location data</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Settings className="w-6 h-6 text-cyan-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Managing Your Cookies</h2>
                </div>
                <div className="space-y-4 text-gray-700">
                  <p className="leading-relaxed">
                    You have control over cookies. Here's how to manage them:
                  </p>
                  <div className="bg-gradient-to-br from-cyan-50 to-turquoise-50 p-6 rounded-xl">
                    <h3 className="font-bold text-gray-900 mb-3">Browser Settings</h3>
                    <p className="text-gray-700 mb-3">
                      Most browsers allow you to refuse cookies or delete them. Please note that disabling certain cookies may impact website functionality.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-cyan-500 rounded-full" />
                        Chrome: Settings → Privacy and Security → Cookies
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-cyan-500 rounded-full" />
                        Firefox: Options → Privacy & Security → Cookies
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-cyan-500 rounded-full" />
                        Safari: Preferences → Privacy → Cookies
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-0 shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Cookies</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use services from trusted third-party providers that may set their own cookies:
                </p>
                <div className="space-y-3">
                  <div className="bg-white p-4 rounded-xl border-l-4 border-cyan-500">
                    <p className="font-semibold text-gray-900">Firebase Authentication</p>
                    <p className="text-sm text-gray-600">For secure user authentication and session management</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border-l-4 border-turquoise-500">
                    <p className="font-semibold text-gray-900">Payment Processors</p>
                    <p className="text-sm text-gray-600">For secure payment processing and fraud prevention</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border-l-4 border-blue-500">
                    <p className="font-semibold text-gray-900">Analytics Services</p>
                    <p className="text-sm text-gray-600">To help us improve our service and user experience</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-0 shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Consent</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By using Fluxara, you consent to our use of cookies as described in this policy. You can withdraw consent at any time by adjusting your browser settings or contacting us.
                </p>
                <div className="bg-gradient-to-r from-cyan-600 to-turquoise-600 p-6 rounded-xl text-white">
                  <p className="font-bold mb-2">Questions about our Cookie Policy?</p>
                  <p className="text-cyan-100">Contact us at support@fluxaraai.com for more information.</p>
                </div>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card className="glass border-0 shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Updates</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicy;
